// Reduces the 2-D pivot model to the 1-D series DynamicChart expects
// ([{ label, count }], the same shape as the dashboard's filters_summary).
//
// A pivot can hold a rows x cols cross, but every chart type in the dashboard
// plots a single series. So the caller picks what to plot:
//   - TOTAL_SERIES (default) -> each row's Total column
//   - a column key           -> that column's slice through the rows
// When no row dimension is assigned, the breakdown lives on the column axis
// and is charted directly.

import { percentOf, VALUE_PERCENT } from "./formatValue";

export const TOTAL_SERIES = "__total__";

/**
 * @param {Object|null} pivot - model from computePivot() / pivotFromAggregate()
 * @param {string} [seriesKey] - TOTAL_SERIES or one of pivot.colKeys[].key
 * @param {string} [valueMode] - VALUE_COUNT | VALUE_PERCENT; percent plots each
 *   value as a share of the grand total so the chart matches the table.
 * @returns {Array<{label: string, count: number}>}
 */
export const pivotToChartData = (pivot, seriesKey = TOTAL_SERIES, valueMode) => {
  if (!pivot) return [];

  const { rowKeys, colKeys, cells, rowTotals, colTotals, grandTotal, isAllRow, isAllCol } =
    pivot;

  // A measure pivot already holds days; a percentage of it means nothing.
  const asValue = (n) =>
    !pivot.measure && valueMode === VALUE_PERCENT ? percentOf(n, grandTotal) : n;

  if (isAllRow) {
    // Only a grand total exists — nothing to break down.
    if (isAllCol) return [];
    return colKeys.map((col) => ({
      label: col.values.join(" ▸ "),
      count: asValue(colTotals.get(col.key) || 0),
    }));
  }

  // Fall back to totals when the requested series no longer exists (e.g. the
  // user dropped the column field after picking a series).
  const useTotal =
    isAllCol ||
    seriesKey === TOTAL_SERIES ||
    !colKeys.some((c) => c.key === seriesKey);

  return rowKeys.map((row) => ({
    label: row.values.join(" ▸ "),
    count: asValue(
      useTotal
        ? rowTotals.get(row.key) || 0
        : cells.get(`${row.key}||${seriesKey}`) || 0
    ),
  }));
};

/**
 * Drops points whose PLOTTED value is 0. A zero slice/bar adds a label and a
 * legend entry with nothing to see, so dashboard pieces hide them.
 *
 * Filters the plotted value, not the raw count, on purpose: in percent mode a
 * tiny share (1 of 2000 -> 0.05%) rounds to 0 in percentOf and would draw as
 * empty anyway. One rule, whatever the value mode.
 * Opt-in: the Builder keeps every row so the chart matches its table.
 */
export const dropZeroPoints = (data) =>
  (data || []).filter((point) => Number(point?.count) !== 0);

/** Series the user can chart: the Total column plus each column value. */
export const seriesOptions = (pivot, totalLabel) => {
  if (!pivot || pivot.isAllCol || pivot.isAllRow) return [];
  return [
    { value: TOTAL_SERIES, label: totalLabel },
    ...pivot.colKeys.map((col) => ({
      value: col.key,
      label: col.values.join(" ▸ "),
    })),
  ];
};
